/* «Багц, төлбөр» — Стандарт 69к (сонгогдоно), Про 129к (тун удахгүй) */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cwApi, type InvoiceDto } from '@/api/carwash';
import { tug } from '@/shared/format';
import { IconCheck, IconClock, IconReceipt } from '@/shared/icons';
import { useTenantScope } from '@/app/providers/TenantScopeProvider';
import { useToast } from '@/app/providers/ToastProvider';

const STANDARD_FEATURES = [
  'Дараалал самбар, ээлж, тооцооны тайлан',
  'Харилцагч + улсын бүртгэлийн лавлагаа',
  'QPay автомат төлбөр (QR)',
  'Гар төлбөр + и-баримтын РД',
];

const PRO_FEATURES = [
  'Стандартын бүх боломж',
  'Угаагч хязгааргүй',
  'Олон салбарын нэгдсэн удирдлага',
];

function dateText(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysLeft(iso?: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return Math.ceil((d.getTime() - Date.now()) / 86_400_000);
}

export function PlanPanel() {
  const toast = useToast();
  const qc = useQueryClient();
  const { tenantId } = useTenantScope();
  const [yearly, setYearly] = useState(false);

  const tenantQ = useQuery({
    queryKey: ['cw', 'tenant-plan', tenantId],
    queryFn: () => cwApi.tenantPlan(tenantId),
  });
  const invoicesQ = useQuery({ queryKey: ['cw', 'invoices'], queryFn: cwApi.myInvoices });

  const tenant = tenantQ.data;
  const pendingInvoice: InvoiceDto | undefined = (invoicesQ.data ?? [])
    .filter((i) => i.TenantId === tenantId && i.Status === 0 && i.Kind === 0)
    .sort((a, b) => Date.parse(b.CreatedOn) - Date.parse(a.CreatedOn))[0];

  const createMut = useMutation({
    mutationFn: () => cwApi.createPlanInvoice(tenantId, 1, yearly ? 12 : 1),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cw', 'invoices'] });
      toast('Нэхэмжлэх үүслээ');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Алдаа гарлаа.'),
  });

  /* Төлвийн badge */
  const isTrial = tenant?.Plan === 0;
  const trialDays = isTrial ? daysLeft(tenant?.TrialEndsOn) : null;
  const planDays = !isTrial ? daysLeft(tenant?.PlanExpiresOn) : null;
  const expired = isTrial ? trialDays !== null && trialDays <= 0 : planDays !== null && planDays <= 0;
  const planLabel = tenant?.Plan === 1 ? 'Стандарт' : tenant?.Plan === 2 ? 'Про' : 'Туршилт';

  const badge = !tenant ? null : expired ? (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-rose-700 bg-rose-50 ring-1 ring-rose-200 rounded-full px-3 py-1.5">
      <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
      {isTrial ? 'Туршилт дууссан' : 'Эрх дууссан'}
    </span>
  ) : isTrial ? (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-full px-3 py-1.5">
      <IconClock size={13} />
      Туршилт · {trialDays ?? '—'} хоног үлдсэн
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-full px-3 py-1.5">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
      {planLabel} · {dateText(tenant.PlanExpiresOn)} хүртэл
    </span>
  );

  const stdPrice = yearly ? 690000 : 69000;
  const proPrice = yearly ? 1290000 : 129000;
  const per = yearly ? '/жил' : '/сар';

  return (
    <div className="rounded-[var(--r)] bg-white ring-1 ring-slate-900/5 shadow-xs p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="w-8 h-8 rounded-[9px] bg-cyan-50 text-[var(--brand1)] flex items-center justify-center"><IconReceipt size={16} /></span>
        <div className="text-[14px] font-bold text-slate-800">Багц, төлбөр</div>
        <div className="ml-auto">{badge}</div>
      </div>

      {/* Сар / Жил */}
      <div className="flex items-center gap-2">
        <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-[12px] p-1 w-[220px]">
          {([false, true] as const).map((y) => (
            <button key={String(y)} onClick={() => setYearly(y)}
              className={'min-h-[36px] rounded-[9px] text-[13px] font-semibold transition ' +
                (yearly === y ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500')}>
              {y ? 'Жилээр' : 'Сараар'}
            </button>
          ))}
        </div>
        {yearly && (
          <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-full px-2.5 py-1">
            2 сар үнэгүй
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Стандарт */}
        <div className="rounded-[var(--r)] ring-2 ring-[var(--brand2)] bg-cyan-50/40 p-4 flex flex-col gap-3">
          <div className="flex items-baseline gap-2">
            <span className="text-[15px] font-extrabold text-slate-900">Стандарт</span>
            <span className="ml-auto text-[20px] font-extrabold text-slate-900">{tug(stdPrice)}</span>
            <span className="text-[12px] text-slate-400 font-medium">{per}</span>
          </div>
          <ul className="flex flex-col gap-1.5">
            {STANDARD_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-1.5 text-[13px] text-slate-700">
                <IconCheck size={14} strokeWidth={2.5} className="text-[var(--brand1)] mt-0.5 shrink-0" />
                {f}
              </li>
            ))}
          </ul>
          {pendingInvoice ? (
            <div className="mt-auto rounded-[10px] bg-white ring-1 ring-amber-200 px-3.5 py-3 flex flex-col gap-1">
              <div className="text-[12px] font-bold text-amber-700">Нэхэмжлэх хүлээгдэж байна</div>
              <div className="text-[13px] text-slate-700">
                {pendingInvoice.InvoiceNo} · <b>{tug(pendingInvoice.Amount)}</b> · {pendingInvoice.PeriodMonths} сар
              </div>
              <div className="text-[12px] text-slate-400 leading-relaxed">
                Шилжүүлгээр төлсний дараа админ баталгаажуулснаар багц идэвхжинэ.
              </div>
            </div>
          ) : (
            <button onClick={() => createMut.mutate()} disabled={createMut.isPending}
              className="mt-auto min-h-[44px] rounded-[10px] text-[14px] font-semibold text-white bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] shadow-md shadow-cyan-600/25 hover:opacity-95 transition disabled:opacity-60">
              {createMut.isPending ? 'Үүсгэж байна…' : (tenant?.Plan === 1 && !expired ? 'Сунгах' : 'Сонгох')}
            </button>
          )}
        </div>

        {/* Про — тун удахгүй */}
        <div className="rounded-[var(--r)] ring-1 ring-slate-200 bg-slate-50/60 p-4 flex flex-col gap-3 opacity-70">
          <div className="flex items-baseline gap-2">
            <span className="text-[15px] font-extrabold text-slate-500">Про</span>
            <span className="text-[10px] font-bold uppercase tracking-wide text-white bg-slate-400 rounded-full px-2 py-0.5">Тун удахгүй</span>
            <span className="ml-auto text-[20px] font-extrabold text-slate-500">{tug(proPrice)}</span>
            <span className="text-[12px] text-slate-400 font-medium">{per}</span>
          </div>
          <ul className="flex flex-col gap-1.5">
            {PRO_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-1.5 text-[13px] text-slate-500">
                <IconCheck size={14} strokeWidth={2.5} className="text-slate-400 mt-0.5 shrink-0" />
                {f}
              </li>
            ))}
          </ul>
          <button disabled
            className="mt-auto min-h-[44px] rounded-[10px] text-[14px] font-semibold text-slate-400 bg-slate-100 ring-1 ring-slate-200 cursor-not-allowed">
            Тун удахгүй
          </button>
        </div>
      </div>

      <div className="text-[11px] text-slate-400">
        Шинэ бүртгэлд 30 хоногийн үнэгүй туршилт, бүх боломж нээлттэй. Эрх дууссан үед ээлж нээх боломжгүй болно.
      </div>
    </div>
  );
}
